import {
	Grid,
	Card,
	Stack,
	Typography,
	useTheme,
	IconButton,
	Menu,
	MenuItem,
	Box
} from "@mui/material";
import { CardContainer } from "../common/components/CardContainer";
import { useQuoteContext } from "../context";
import { useMemo, useState } from "react";
import { text_ellipsis } from "../styles";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import { FunctionsOutlined, InfoOutlined, MoreVert } from "@mui/icons-material";
import { useQuoteStatisticsData } from "./useQuoteStatisticsData";
import {
	MARGIN_VIEW_NON_EXCLUDED,
	MARGIN_VIEW_STANDARD,
	marginViewScopeForPanelKey,
	readStoredMarginView,
	writeStoredMarginView
} from "../common/marginViewStorage";

export const QuoteStatistics = ({ isCollapsed }) => {
	const quote = useQuoteContext();
	const { recurringPrefix, quoteStatisticsData } = useQuoteStatisticsData(isCollapsed);
	const numberOfDivs = useMemo(
		() => quoteStatisticsData.filter((data) => !data.isHidden).length,
		[quoteStatisticsData]
	);

	let recurringPerType = {};
	if (recurringPrefix === "Annual") {
		recurringPerType = quote.AnnualRecurringPerCycle;
	} else if (recurringPrefix === "Monthly") {
		recurringPerType = quote.MonthlyRecurringPerCycle;
	}

	return (
		<Stack
			direction={"row"}
			gap="8px"
			sx={{
				flexWrap: "nowrap",
				flex: numberOfDivs,
				overflow: "hidden",
				minWidth: {
					md: "100%",
					lg: "auto",
					xl: 0
				}
			}}
		>
			{quoteStatisticsData.map(
				(data) =>
					!data.isHidden && (
						<CardContainer
							key={data.key}
							sx={{
								flex: data.flex ?? 1,
								minHeight: isCollapsed ? "54px" : "128px",
								height: "auto",
								minWidth: {
									md: isCollapsed ? "100px" : "180px",
									lg: "180px",
									xl: isCollapsed ? "185px" : "230px"
								}
							}}
							styleOutlineOverrides={{
								display: "flex",
								flex: "1",
								padding: "8px 16px",
								height: "100%",
								overflow: "hidden"
							}}
						>
							<QuoteCard
								isCollapsed={isCollapsed}
								data={data.value}
								marginViewScope={marginViewScopeForPanelKey(data.key)}
								annualRecurringPerType={recurringPerType}
								enableMonthlyRecurring={recurringPrefix === "Monthly"}
							/>
						</CardContainer>
					)
			)}
		</Stack>
	);
};

export const OneTimeTotal = () => {
	const quote = useQuoteContext();
	const { recurringPrefix } = useQuoteStatisticsData(true);
	return (
		<CardContainer
			key={"quote_total"}
			sx={{
				flex: 1,
				minHeight: "54px",
				height: "auto",
				minWidth: {
					md: "90px",
					lg: "200px",
					xl: "300px"
				}
			}}
			styleOutlineOverrides={{
				display: "flex",
				flex: "1",
				padding: "8px 16px",
				height: "100%"
			}}
		>
			<QuoteCard
				isCollapsed={true}
				data={[
					{
						title: "One Time Total",
						isHidden: false,
						value: quote.formatCurrency(quote.QuoteTotal),
						id: "test-one-time-total",
						icon: <FunctionsOutlined fontSize="medium" sx={{ color: "icon.primary" }} />
					}
				]}
				annualRecurringPerType={(() => {
					if (recurringPrefix === "Annual") {
						return quote.AnnualRecurringPerCycle;
					} else if (recurringPrefix === "Monthly") {
						return quote.MonthlyRecurringPerCycle;
					}
					return {};
				})()}
				enableMonthlyRecurring={recurringPrefix === "Monthly"}
			/>
		</CardContainer>
	);
};

const METRIC_ICON_RAIL_WIDTH = 32;
/** Expanded detail block height ≈ 3 × other_values lines — keeps card height when swapping views. */
const METRIC_DETAIL_BLOCK_MIN_HEIGHT = 62;

const QuoteStatisticMetric = ({
	title,
	value,
	icon,
	other_values,
	id,
	key,
	index,
	isCollapsed,
	marginToggle,
	data,
	enableMonthlyRecurring,
	open,
	onPopoverOpen,
	onPopoverClose,
	theme,
	showNonExcludedMargin
}) => {
	const hasMarginToggle = Boolean(marginToggle?.nonExcludedFields?.length);
	const showNonExcludedView = hasMarginToggle && showNonExcludedMargin;
	const primaryNonExcluded = marginToggle?.nonExcludedFields?.[0];
	const collapsedValue =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.value : value;
	const collapsedId =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.id : id;
	const displayTitle =
		showNonExcludedView && isCollapsed && primaryNonExcluded
			? primaryNonExcluded.label
			: title;
	const secondaryNonExcluded = marginToggle?.nonExcludedFields?.[1];
	const hasDetailSection =
		Boolean(other_values?.length) || hasMarginToggle;
	const expandedPrimaryLabel =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.label : title;
	const expandedPrimaryValue =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.value : value;
	const expandedPrimaryId =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.id : id;
	const expandedPrimaryTestValue =
		showNonExcludedView && primaryNonExcluded ? primaryNonExcluded.value : collapsedValue;
	const detailValueColorSource = showNonExcludedView ? expandedPrimaryValue : value;

	const valueColor = (val) =>
		val < 0 ? theme.palette.text.error : theme.palette.text.primary;

	return (
		<Stack key={`title-stack-${title}`} direction="column">
			<Grid container gap={"8px"}>
				{index === 0 ? null : (
					<Grid item xs={12}>
						<Divider />
					</Grid>
				)}
				<Grid
					item
					container
					sx={{
						display: "flex",
						alignItems: "flex-start",
						flexWrap: "nowrap"
					}}
					gap="12px"
				>
					<Grid
						item
						sx={{
							width: METRIC_ICON_RAIL_WIDTH,
							minWidth: METRIC_ICON_RAIL_WIDTH,
							flexShrink: 0,
							display: "flex",
							justifyContent: "center",
							alignItems: "flex-start"
						}}
					>
						{icon}
					</Grid>
					<Grid
						item
						sx={{
							display: "flex",
							flexDirection: "column",
							flex: 1,
							minWidth: 0,
							...text_ellipsis
						}}
						gap="4px"
					>
						{isCollapsed ? (
							<>
								<Stack direction="row" alignItems="center" gap="4px">
									<Typography
										sx={{
											fontSize: "12px",
											minHeight: "16px",
											whiteSpace: "nowrap",
											width: "100%",
											letterSpacing: "0.32px",
											textAlign: "left",
											color: theme.palette.text.secondary,
											...text_ellipsis,
											"&:hover": {
												fontWeight: data[0].showPopover ? 800 : 400
											}
										}}
										aria-owns={
											open && data[0].showPopover
												? "mouse-over-popover"
												: undefined
										}
										aria-haspopup="true"
										onMouseEnter={onPopoverOpen}
										onMouseLeave={onPopoverClose}
										data-testid={key}
									>
										{displayTitle}
									</Typography>
									{data[0].showPopover && enableMonthlyRecurring && (
										<InfoOutlined
											fontSize="small"
											sx={{
												color: "icon.secondary",
												fontSize: "14px",
												marginTop: "2px"
											}}
											titleAccess={
												<div
													dangerouslySetInnerHTML={{
														__html:
															"Monthly Recurring Revenue is &#013; the monthly equivalent &#013; of all selected billing frequencies"
													}}
												/>
											}
										/>
									)}
								</Stack>
								<Typography
									data-testid={collapsedValue}
									title={collapsedValue}
									id={collapsedId}
									sx={{
										fontWeight: 400,
										lineHeight: "22px",
										minHeight: "18px",
										fontSize: "16px",
										width: "100%",
										color: valueColor(collapsedValue),
										...text_ellipsis
									}}
								>
									{collapsedValue}
								</Typography>
							</>
						) : (
							<>
								<Stack direction="row" alignItems="flex-start" gap="4px">
									<Typography
										sx={{
											fontSize: "12px",
											minHeight: "16px",
											lineHeight: "16px",
											width: "100%",
											letterSpacing: "0.32px",
											textAlign: "left",
											color: theme.palette.text.secondary,
											...(showNonExcludedView
												? {
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														overflow: "hidden",
														whiteSpace: "normal",
														wordBreak: "break-word"
													}
												: {
														whiteSpace: "nowrap",
														...text_ellipsis
													}),
											"&:hover": {
												fontWeight:
													!showNonExcludedView && data[0].showPopover
														? 800
														: 400
											}
										}}
										aria-owns={
											open && data[0].showPopover && !showNonExcludedView
												? "mouse-over-popover"
												: undefined
										}
										aria-haspopup="true"
										onMouseEnter={
											!showNonExcludedView ? onPopoverOpen : undefined
										}
										onMouseLeave={
											!showNonExcludedView ? onPopoverClose : undefined
										}
										data-testid={key}
										title={expandedPrimaryLabel}
									>
										{expandedPrimaryLabel}
									</Typography>
									{!showNonExcludedView &&
										data[0].showPopover &&
										enableMonthlyRecurring && (
											<InfoOutlined
												fontSize="small"
												sx={{
													color: "icon.secondary",
													fontSize: "14px",
													marginTop: "2px"
												}}
												titleAccess={
													<div
														dangerouslySetInnerHTML={{
															__html:
																"Monthly Recurring Revenue is &#013; the monthly equivalent &#013; of all selected billing frequencies"
														}}
													/>
												}
											/>
										)}
								</Stack>
								<Typography
									data-testid={expandedPrimaryTestValue}
									title={expandedPrimaryValue}
									id={expandedPrimaryId}
									sx={{
										fontWeight: 400,
										lineHeight: "22px",
										minHeight: "22px",
										fontSize: "16px",
										width: "100%",
										color: valueColor(expandedPrimaryValue),
										...text_ellipsis
									}}
								>
									{expandedPrimaryValue}
								</Typography>
							</>
						)}
					</Grid>
				</Grid>
				{!isCollapsed && hasDetailSection && (
					<Grid
						container
						item
						sx={{
							marginLeft: `${METRIC_ICON_RAIL_WIDTH + 12}px`,
							flexDirection: "column",
							minHeight: METRIC_DETAIL_BLOCK_MIN_HEIGHT,
							justifyContent: "flex-start"
						}}
						gap="4px"
					>
						{showNonExcludedView && secondaryNonExcluded ? (
							<Grid item sx={{ width: "100%", minWidth: 0 }}>
								<Typography
									component="div"
									sx={{
										fontWeight: 400,
										lineHeight: "18px",
										fontSize: "14px",
										letterSpacing: "0.16px",
										textAlign: "left",
										color: valueColor(detailValueColorSource),
										width: "100%",
										minWidth: 0
									}}
								>
									<Typography
										component="span"
										display="block"
										title={secondaryNonExcluded.label}
										sx={{
											fontSize: "14px",
											lineHeight: "18px",
											letterSpacing: "0.16px",
											color: "inherit",
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											wordBreak: "break-word"
										}}
									>
										{secondaryNonExcluded.label}:
									</Typography>
									<Typography
										component="span"
										display="block"
										id={secondaryNonExcluded.id}
										data-testid={secondaryNonExcluded.value}
										title={secondaryNonExcluded.value}
										sx={{
											fontSize: "14px",
											lineHeight: "18px",
											minHeight: "18px",
											color: "inherit",
											...text_ellipsis
										}}
									>
										{secondaryNonExcluded.value}
									</Typography>
								</Typography>
							</Grid>
						) : (
							other_values?.map(({ sub_title, subtitle_value, isHidden, id: subId }) => {
								if (isHidden) return null;
								return (
									<Grid item key={`other_val-${sub_title}`}>
										<Typography
											sx={{
												fontWeight: 400,
												lineHeight: "18px",
												fontSize: "14px",
												letterSpacing: "0.16px",
												textAlign: "left",
												minHeight: "18px",
												color: valueColor(value),
												...text_ellipsis
											}}
										>
											{sub_title}:&nbsp;&nbsp;
											<span
												data-testid={subtitle_value}
												id={subId}
												title={subtitle_value}
											>
												{subtitle_value}
											</span>
										</Typography>
									</Grid>
								);
							})
						)}
					</Grid>
				)}
			</Grid>
		</Stack>
	);
};

export const QuoteCard = ({
	data,
	isCollapsed,
	marginViewScope,
	annualRecurringPerType,
	enableMonthlyRecurring
}) => {
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState(null);
	const [marginMenuAnchorEl, setMarginMenuAnchorEl] = useState(null);
	const [marginView, setMarginView] = useState(() => readStoredMarginView(marginViewScope));
	const quote = useQuoteContext();
	const marginMetric = data?.find(
		(metric) => !metric.isHidden && metric.marginToggle?.nonExcludedFields?.length
	);
	const hasMarginViewMenu = Boolean(marginMetric);
	const showNonExcludedMargin = marginView === MARGIN_VIEW_NON_EXCLUDED;

	const handlePopoverOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	const handleMarginMenuOpen = (event) => {
		setMarginMenuAnchorEl(event.currentTarget);
	};

	const handleMarginMenuClose = () => {
		setMarginMenuAnchorEl(null);
	};

	const handleMarginViewSelect = (view) => {
		setMarginView(view);
		writeStoredMarginView(marginViewScope, view);
		handleMarginMenuClose();
	};

	const open = Boolean(anchorEl);
	const marginMenuOpen = Boolean(marginMenuAnchorEl);
	return (
		<>
			<Box
				sx={{
					display: "flex",
					width: "100%",
					height: "100%",
					alignItems: "flex-start",
					minWidth: 0
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
					<Card
						sx={{
							boxShadow: "none",
							width: "100%",
							display: "flex",
							flexDirection: "column",
							height: "100%",
							padding: "1px",
							gap: "6px",
							justifyContent: "center",
							overflow: "hidden"
						}}
					>
						{data &&
							data
								?.filter((d) => !d.isHidden)
								.map((metric, index) => (
									<QuoteStatisticMetric
										key={metric.title}
										{...metric}
										index={index}
										isCollapsed={isCollapsed}
										data={data}
										enableMonthlyRecurring={enableMonthlyRecurring}
										open={open}
										onPopoverOpen={handlePopoverOpen}
										onPopoverClose={handlePopoverClose}
										theme={theme}
										showNonExcludedMargin={showNonExcludedMargin}
									/>
								))}
					</Card>
				</Box>
				{hasMarginViewMenu && (
					<Box
						sx={{
							width: 22,
							minWidth: 22,
							flexShrink: 0,
							alignSelf: "flex-start",
							marginTop: "-2px",
							marginRight: "-4px"
						}}
					>
						<IconButton
							size="small"
							data-testid={`margin-view-menu-${marginMetric.id}`}
							onClick={handleMarginMenuOpen}
							aria-label="Margin display options"
							aria-controls={
								marginMenuOpen ? "quote-statistic-margin-menu" : undefined
							}
							aria-haspopup="true"
							aria-expanded={marginMenuOpen}
							disableRipple
							sx={{
								padding: 0,
								minWidth: 22,
								width: 22,
								height: 22,
								color: "icon.secondary",
								borderRadius: "4px",
								"&:hover": {
									backgroundColor: "action.hover"
								}
							}}
						>
							<MoreVert sx={{ fontSize: 18 }} />
						</IconButton>
						<Menu
							id="quote-statistic-margin-menu"
							anchorEl={marginMenuAnchorEl}
							open={marginMenuOpen}
							onClose={handleMarginMenuClose}
							disableScrollLock
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
						>
							<MenuItem
								selected={marginView === MARGIN_VIEW_STANDARD}
								onClick={() => handleMarginViewSelect(MARGIN_VIEW_STANDARD)}
								data-testid="margin-view-standard"
							>
								Standard Gross Profit
							</MenuItem>
							<MenuItem
								selected={marginView === MARGIN_VIEW_NON_EXCLUDED}
								onClick={() => handleMarginViewSelect(MARGIN_VIEW_NON_EXCLUDED)}
								data-testid="margin-view-non-excluded"
							>
								Non-Excluded Margins
							</MenuItem>
						</Menu>
					</Box>
				)}
			</Box>
			<Popover
				id="mouse-over-popover"
				open={open && data[0].showPopover}
				anchorEl={anchorEl}
				container={anchorEl?.parentNode}
				sx={{
					minWidth: 359,
					maxWidth: 359,
					minHeight: 284,
					marginTop: Object?.keys(annualRecurringPerType || {}).length > 4 ? 10 : 5,
					pointerEvents: "none",
					boxSizing: "border-box"
				}}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: -320
				}}
				transformOrigin={{
					vertical: "center",
					horizontal: "left"
				}}
				onClose={handlePopoverClose}
				disableRestoreFocus
			>
				<Stack
					sx={{ width: "270px" }}
					justifyContent="space-between"
					alignItems="center"
					backgroundColor={theme.palette.background.primary}
					divider={<Divider orientation="horizontal" flexItem />}
				>
					{Object.keys(annualRecurringPerType || {}).map((key) => (
						<Stack
							direction="row"
							key={key}
							spacing={1}
							sx={{ width: "240px", height: "50px", p: 1 }}
							justifyContent="space-between"
							alignItems="center"
						>
							<Typography variant="body2" sx={{ fontSize: "14px" }}>
								{key}
							</Typography>
							<Typography variant="body2" fontWeight={400}>
								{quote.formatCurrency(annualRecurringPerType[key])}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Popover>
		</>
	);
};
